// src/pages/ProductPage.js
// IMPROVED VERSION — trust-cliff additions only. Checkout/Stripe logic is unchanged
// from the deployed server version. New vs. original:
//   1. Richer product descriptions (names the biases each title trains)
//   2. Evidence / credibility banner near the top
//   3. "What your license includes" box (with commercial vs. academic pricing)
// Everything else is byte-for-byte the deployed code.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';

const formatPrice = (price) => {
  return price.toFixed(2);
};

// Create a single Stripe-JS promise (publishable key comes from .env build-time)
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
);

const ProductPage = () => {
  /* --------------------------------------------------  product list  */
  const [products] = useState([
    {
      id: 'part1_regular',
      name: 'MISSING: The Search for Terry Hughes',
      description:
        'The first MISSING game. An interactive investigation that trains you to recognize and counter three biases: Confirmation Bias, the Fundamental Attribution Error, and the Bias Blind Spot.',
      price: 150,
      academicPrice: 29.50,
      application: 'PART1',
      priceId: 'price_1REwKwEtLbNCaho645tRQfC3',          //production commercial price
      academicPriceId: 'price_1RWfgrEtLbNCaho6cGkRw1nz',  // production academic price
      // priceId: 'price_1SCOnHEtLbNCaho6DVNcLgEX',             // test commercial price
      // academicPriceId: 'price_1SCOM6EtLbNCaho6kPEAI8yx',     // test academic price     
    },
    {
      id: 'part2_regular',
      name: 'MISSING: The Final Secret',
      description:
        'The second MISSING game. Continue the investigation while training three more biases: Anchoring Bias, the Representativeness Heuristic, and Projection Bias.',
      price: 150,
      academicPrice: 29.50,
      application: 'PART2',
      priceId: 'price_1REwLIEtLbNCaho6frC1Ip8F',         //production commercial price
      academicPriceId: 'price_1RWfi4EtLbNCaho61NXusucu', // production academic price
      // priceId: 'price_1SCOnHEtLbNCaho6DVNcLgEX',            //test commercial price
      // academicPriceId: 'price_1SCOMhEtLbNCaho6fj76z62e',    //test academic price
    },
  ]);

  /* --------------------------------------------------  UI state  */
  const [cart, setCart] = useState([]);
  const [qty,  setQty]  = useState({});   // { productId: number }
  const [email, setEmail] = useState('');
  const [isAcademic, setIsAcademic] = useState(false);
  const [buyingForSelf, setBuyingForSelf] = useState(true); // New state for purchase intent
  const [academicProof, setAcademicProof] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const navigate = useNavigate();

  // Calculate total quantity in cart
  const totalQuantity = cart.reduce((sum, item) => sum + item.qty, 0);
  
  // Auto-set buying intent based on quantity
  useEffect(() => {
    if (totalQuantity > 1) {
      setBuyingForSelf(false); // Multiple licenses = buying for others
    }
  }, [totalQuantity]);

  /* --------------------------------------------------  cart helpers  */
  const addToCart = (product) => {
    const q   = qty[product.id] || 1;
    const idx = cart.findIndex(i => i.id === product.id);
    if (idx === -1)
      setCart([...cart, { ...product, qty:q }]);
    else {
      const next=[...cart];
      next[idx] = { ...next[idx], qty:q };
      setCart(next);
    }
    // toast
    setSuccessMessage(`${product.name} added to cart`);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const removeFromCart = (id) => {
    const product = cart.find(item => item.id === id);
    setCart(cart.filter((item) => item.id !== id));
    
    if (product) {
      setSuccessMessage(`${product.name} removed from cart`);
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    }
  };

  /* --------------------------------------------------  academic upload  */
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadSuccess(false);
    setLoading(true);
    setError("");

    const formData = new FormData();
    formData.append('file', file);
    formData.append('email', email);

    try {
      const res = await fetch('/upload-academic-proof', {
        method: 'POST',
        body: formData,
      });
      const json = await res.json();
      
      if (json.id && (json.success !== false)) {
        setAcademicProof(json);
        setUploadSuccess(true);
        setSuccessMessage(json.message || "Academic proof uploaded successfully");
        console.log('Academic proof uploaded:', json);
        
        if (json.warning) {
          console.warn("Upload warning:", json.warning);
        }
      } else {
        console.error('Upload failed:', json);
        setError(json.message || "Upload failed. Please try again.");
      }
    } catch (err) {
      console.error('Upload error', err);
      setError("Upload failed. Please try again.");
    } finally {
      setLoading(false);
      
      if (successMessage) {
        setTimeout(() => {
          setSuccessMessage("");
        }, 3000);
      }
    }
  };

  /* --------------------------------------------------  checkout  */
  const handleCheckout = async () => {
    /* basic validations */
    if (!cart.length) {
      setError("Your cart is empty");
      return;
    }
    
    if (!email) {
      setError("Please enter your email address");
      return;
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    if (isAcademic) {
      const isEdu = email.toLowerCase().endsWith('.edu');
      
      if (!isEdu && (!academicProof || !uploadSuccess)) {
        setError("Upload academic proof or use a .edu email");
        return;
      }
    }

    setLoading(true);
    setError("");

    /* build request payload */
    const items = cart.map((p) => ({
      id: p.id,
      application: p.application,
      priceId: isAcademic ? p.academicPriceId : p.priceId,
      quantity: p.qty
    }));

    const endpoint = isAcademic
      ? '/create-academic-checkout-session'
      : '/create-checkout-session';

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          products: items,
          email,
          academicProof,
          buyingForSelf, // Include the buying intent in the request
        }),
      });

      const data = await res.json();
      if (data.error) {
        console.error('Server error', data.error);
        setError(data.error);
        return;
      }

      /* redirect via Stripe JS */
      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({
        sessionId: data.id,
      });
      if (error) {
        console.error('Stripe error', error);
        setError(error.message);
      }
    } catch (err) {
      console.error('Checkout flow error', err);
      setError('Payment system error. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  /* --------------------------------------------------  helpers  */
  const total = cart.reduce((sum, p) => {
    const unit = isAcademic ? p.academicPrice : p.price;
    return sum + unit * p.qty;
  }, 0);

  /* --------------------------------------------------  render  */
  return (
    <div style={{ padding: "1rem", maxWidth: 700, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
        <h1 style={{ fontSize: "1.5rem", margin: 0 }}>MISSING Game Licenses</h1>
        <button 
          onClick={() => navigate("/")}
          style={{ 
            padding: "0.5rem 1rem", 
            backgroundColor: "#2196F3", 
            color: "white", 
            border: "none",
            borderRadius: "4px",
            cursor: "pointer" 
          }}
        >
          Back to Home
        </button>
      </div>

      {/* --------------------------------------------------  evidence / credibility banner  */}
      <div style={{
        border: "1px solid #c8e6c9",
        backgroundColor: "#f1f8e9",
        padding: "1rem",
        marginBottom: "1rem",
        borderRadius: "4px"
      }}>
        <div style={{ fontSize: "1.05rem", fontWeight: "bold", marginBottom: "0.4rem", color: "#1b5e20" }}>
          Research-proven cognitive bias training
        </div>
        <p style={{ margin: "0 0 0.5rem 0", color: "#333", lineHeight: 1.5 }}>
          Developed for U.S. intelligence analysts under the IARPA Sirius program. In
          peer-reviewed laboratory research, a single ~90-minute playthrough cut players&rsquo; measured
          cognitive bias by about <strong>32% immediately</strong> and <strong>23% two to three
          months later</strong>. In a separate peer-reviewed field study at HEC Paris, trained
          players chose the biased option just 59% of the time versus 72% untrained &mdash; about
          a <strong>19% drop</strong> in biased decisions on an unrelated, high-stakes business
          case, evidence the training transfers to the real world.
        </p>
        <div style={{ fontSize: "0.85rem", color: "#555", marginBottom: "0.35rem" }}>
          Featured in The Atlantic &middot; The New York Times &middot; Forbes &middot; Harvard Business Review &middot; Charles Schwab&rsquo;s Choiceology
        </div>
        <div style={{ fontSize: "0.85rem", color: "#2e7d32", fontWeight: "bold" }}>
          Browser-based &middot; about 90 minutes &middot; results that last
        </div>
      </div>
      
      {/* Success Message */}
      {successMessage && (
        <div style={{ 
          color: "green", 
          marginBottom: "1rem", 
          padding: "0.5rem", 
          backgroundColor: "#e8f5e9",
          borderRadius: "4px" 
        }}>
          {successMessage}
        </div>
      )}
      
      {/* Error Message */}
      {error && (
        <div style={{ 
          color: "red", 
          marginBottom: "1rem", 
          padding: "0.5rem", 
          backgroundColor: "#ffebee",
          borderRadius: "4px" 
        }}>
          {error}
        </div>
      )}
      
      {/* Product List Section */}
      <div style={{ 
        border: "1px solid #ccc", 
        padding: "1rem", 
        marginBottom: "1rem",
        borderRadius: "4px" 
      }}>
        <h2 style={{ fontSize: "1.2rem", marginTop: 0 }}>Available Products</h2>
        
        {products.map((product) => (
          <div 
            key={product.id}
            style={{ 
              marginBottom: "1rem", 
              padding: "0.75rem", 
              border: "1px solid #ddd",
              borderRadius: "4px"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <h3 style={{ fontSize: "1.1rem", margin: "0 0 0.5rem 0" }}>{product.name}</h3>
                <p style={{ margin: "0 0 0.5rem 0", color: "#666" }}>{product.description}</p>
                <div style={{display:"flex",alignItems:"center",gap:"0.5rem"}}>
                  <label>Qty:</label>
                  <input type="number" min={1}
                         value={qty[product.id] || 1}
                         onChange={e=>setQty({...qty,[product.id]:Math.max(1,+e.target.value||1)})}
                         style={{width:"60px"}}/>
                </div>
                <div style={{fontWeight:"bold",marginTop:"0.25rem"}}>
                  Unit&nbsp;Price:&nbsp;$
                  {formatPrice(isAcademic ? product.academicPrice : product.price)}
                </div>
              </div>
              <button
                onClick={() => addToCart(product)}
                style={{
                  padding: "0.5rem",
                  backgroundColor: "#4CAF50",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "0.9rem",
                  cursor: "pointer"
                }}
              >
                Add to Cart
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* --------------------------------------------------  what your license includes  */}
      <div style={{
        border: "1px solid #bbdefb",
        backgroundColor: "#f5fbff",
        padding: "1rem",
        marginBottom: "1rem",
        borderRadius: "4px"
      }}>
        <h2 style={{ fontSize: "1.2rem", marginTop: 0 }}>What your license includes</h2>
        <ul style={{ margin: 0, paddingLeft: "1.2rem", color: "#333", lineHeight: 1.6 }}>
          <li>One license covers <strong>one user for one game</strong>.</li>
          <li>Played in your web browser &mdash; <strong>nothing to install</strong>. Access stays open until the user completes the game.</li>
          <li>On completion, the user receives a <strong>PDF completion certificate</strong>.</li>
          <li>The license buyer can confirm each user&rsquo;s progress anytime in the admin dashboard.</li>
          <li>Buying more than one? You&rsquo;ll get a <strong>Buyer account</strong> to assign licenses to your users.</li>
        </ul>
        <div style={{ marginTop: "0.6rem", fontSize: "0.9rem", color: "#555" }}>
          <strong>Pricing:</strong> Commercial $150 per user, per game &middot; Academic $29.50 per
          user, per game (check &ldquo;I qualify for academic pricing&rdquo; below &mdash; a .edu
          email or proof of academic status is required).
        </div>
      </div>
      
      {/* Cart Section */}
      <div style={{ 
        border: "1px solid #ccc", 
        padding: "1rem", 
        marginBottom: "1rem",
        borderRadius: "4px" 
      }}>
        <h2 style={{ fontSize: "1.2rem", marginTop: 0 }}>Your Cart</h2>
        
        {cart.length === 0 ? (
          <p>Your cart is empty</p>
        ) : (
          <div>
            {cart.map((item) => (
              <div 
                key={item.id}
                style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center",
                  padding: "0.5rem 0",
                  borderBottom: "1px solid #eee"
                }}
              >
                <div>
                  <div style={{ fontWeight: "bold" }}>{item.name}</div>
                  <div style={{ fontSize:"0.9rem", color:"#666" }}>
                    {item.qty} × ${formatPrice(isAcademic ? item.academicPrice : item.price)}
                  </div>
                </div>
                <div>
                  <span style={{ marginRight:"1rem" }}>
                    ${formatPrice((isAcademic ? item.academicPrice : item.price) * item.qty)}
                  </span>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    style={{
                      padding: "0.25rem 0.5rem",
                      backgroundColor: "#f44336",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      fontSize: "0.8rem",
                      cursor: "pointer"
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center",
              fontWeight: "bold",
              marginTop: "1rem",
              paddingTop: "0.5rem",
              borderTop: "1px solid #ccc"
            }}>
              <span>Total ({totalQuantity} license{totalQuantity !== 1 ? 's' : ''}):</span>
              <span>${formatPrice(total)}</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Checkout Information Section */}
      <div style={{ 
        border: "1px solid #ccc", 
        padding: "1rem", 
        marginBottom: "1rem",
        borderRadius: "4px" 
      }}>
        <h2 style={{ fontSize: "1.2rem", marginTop: 0 }}>Checkout Information</h2>
        
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", marginBottom: "0.5rem" }}>Email Address:</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ 
              width: "100%", 
              padding: "0.5rem",
              borderRadius: "4px",
              border: "1px solid #ccc"
            }}
            placeholder="you@example.com"
          />
        </div>
        
        {/* License Usage Intent - only show for single license */}
        {totalQuantity === 1 && (
          <div style={{ 
            marginBottom: "1rem",
            padding: "0.75rem",
            backgroundColor: "#f0f8ff",
            borderRadius: "4px",
            border: "1px solid #2196F3"
          }}>
            <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>
              Who will use this license?
            </label>
            <div style={{ marginBottom: "0.5rem" }}>
              <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="buyingIntent"
                  value="self"
                  checked={buyingForSelf}
                  onChange={() => setBuyingForSelf(true)}
                  style={{ marginRight: "0.5rem" }}
                />
                <span>I will use it myself (User account)</span>
              </label>
            </div>
            <div>
              <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="buyingIntent"
                  value="others"
                  checked={!buyingForSelf}
                  onChange={() => setBuyingForSelf(false)}
                  style={{ marginRight: "0.5rem" }}
                />
                <span>I'm buying for someone else (Buyer account)</span>
              </label>
            </div>
            <div style={{ marginTop: "0.5rem", fontSize: "0.85rem", color: "#666" }}>
              {buyingForSelf ? 
                "You'll get direct access to play the game after registration." :
                "You'll be able to assign this license to another user through the Buyer Dashboard."
              }
            </div>
          </div>
        )}
        
        {/* Show note for multiple licenses */}
        {totalQuantity > 1 && (
          <div style={{ 
            marginBottom: "1rem",
            padding: "0.75rem",
            backgroundColor: "#fff3cd",
            borderRadius: "4px",
            border: "1px solid #ffc107"
          }}>
            <strong>Note:</strong> You're purchasing {totalQuantity} licenses. You'll receive a Buyer account 
            to manage and assign these licenses to users.
          </div>
        )}
        
        <div style={{ 
          display: "flex", 
          alignItems: "center",
          marginBottom: "1rem"
        }}>
          <input
            type="checkbox"
            id="academicPricing"
            checked={isAcademic}
            onChange={(e) => {
              const checked = e.target.checked;
              setIsAcademic(checked);

              if (!checked) {
                setAcademicProof(null);
                setUploadSuccess(false);
              }
            }}
            style={{ marginRight: "0.5rem" }}
          />
          <label htmlFor="academicPricing">
            I qualify for academic pricing
          </label>
        </div>
        
        {isAcademic && !email.toLowerCase().endsWith('.edu') && (
          <div style={{ marginBottom: "1rem" }}>
            <label style={{ display: "block", marginBottom: "0.5rem" }}>
              Upload Proof of Academic Status:
            </label>
            <input
              type="file"
              onChange={handleFileUpload}
              style={{ 
                width: "100%", 
                padding: "0.5rem",
                borderRadius: "4px",
                border: "1px solid #ccc"
              }}
              accept=".pdf,.jpg,.jpeg,.png"
            />
            {uploadSuccess && academicProof && (
              <div style={{ 
                marginTop: "0.5rem", 
                color: "green",
                fontSize: "0.9rem"
              }}>
                Document uploaded successfully
              </div>
            )}
          </div>
        )}
        
        <button 
          onClick={handleCheckout} 
          disabled={loading || !cart.length || !email}
          style={{ 
            width: "100%",
            padding: "0.75rem 1rem",
            backgroundColor: (!cart.length || !email) ? "#cccccc" : "#4CAF50",
            color: "white",
            border: "none",
            borderRadius: "4px",
            fontSize: "1rem",
            fontWeight: "bold",
            cursor: (loading || !cart.length || !email) ? "default" : "pointer"
          }}
        >
          {loading ? "Processing..." : "Proceed to Checkout"}
        </button>
      </div>
    </div>
  );
};

export default ProductPage;
